
import { SelfContainedCommand, CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { Insert, CommandExecutor, NodeType } from "../../services/doc/internal/yDocHighLevelAPI";
import { saveToDatabase } from './utils';
import { ObjectId } from "bson";
import { createFileLogger } from '../../../../utils/logger';
import { entityResolver } from '../../../../services/entityResolver';
import { docsCRDTService } from '../../services/crdt/docsCRDTService';

const log = createFileLogger('DOCS_COMMANDS');

export const docsInsertTableCommand: SelfContainedCommand = {
    id: 'docs_insert_table',
    description: 'Insert a new table into the document with specified rows and columns.',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',
    
    schema: {
        parameters: [
            {
                name: 'llmId',
                type: 'string',
                description: 'The unique LLM-friendly ID of the document entity to insert the table into.',
                required: true,
                examples: ['doc-research-paper', 'doc-meeting-notes-2024']
            },
            {
                name: 'rows',
                type: 'number',
                description: 'Number of rows in the table (default: 3)',
                required: false,
                defaultValue: 3
            },
            {
                name: 'cols',
                type: 'number',
                description: 'Number of columns in the table (default: 3)',
                required: false,
                defaultValue: 3
            },
            {
                name: 'hasHeader',
                type: 'boolean',
                description: 'Whether the first row should be a header row (default: true)',
                required: false,
                defaultValue: true
            },
            {
                name: 'location',
                type: 'object',
                description: 'Where to insert the table. Can be simple like {at: "end"} or node-based like {nodeId: "node-id", position: "after"}',
                required: true,
                examples: [{ at: 'end' }, { nodeId: 'some-node-id', position: 'after' }]
            },
            {
                name: 'description',
                type: 'string',
                description: 'Description of the table insertion',
                required: false,
                defaultValue: 'Insert table'
            }
        ]
    },

    examples: {
        good: [
            {
                input: { llmId: 'doc-meeting-notes', rows: 4, cols: 3, location: { at: 'end' } },
                expectedOutput: createSuccessResult('Inserted 4x3 table successfully', { inserted: true, rows: 4, cols: 3 }),
                description: 'Insert a 4x3 table at the end of the document.'
            }
        ],
        bad: [
            {
                input: { llmId: 'doc-does-not-exist', location: { at: 'end' } },
                error: 'Entity not found',
                why: 'The specified llmId does not correspond to an existing document.',
                fix: 'Use a valid llmId from the workspace context.'
            }
        ]
    },

    performance: {
        expectedDuration: 800,
        complexity: 'medium'
    },

    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
        const startTime = Date.now();
        const { llmId, rows = 3, cols = 3, hasHeader = true, location, description } = params;

        try {
            log.log('[docs_insert_table] INPUT:', { llmId, rows, cols, hasHeader, location, description });

            const resolved = await entityResolver.resolveDoc(llmId, context);
            if (!resolved.entity) {
                return createErrorResult('Entity not found', `No document found with llmId: ${llmId}`, 'validation');
            }

            let ydoc;
            let docId;

            if (resolved.windowId) {
                const { getDocsAppStore } = await import('../../store/docsAppStore');
                const docsStore = getDocsAppStore(resolved.windowId, context.spaceId);
                const state = docsStore.getState();
                if (state.ydoc && state.docId) {
                    ydoc = state.ydoc;
                    docId = state.docId;
                    log.log('[docs_insert_table] Using open document from spaceStore');
                }
            }
            
            if (!ydoc) {
                const entityId = resolved.entity._id;
                const { doc } = await docsCRDTService.getYDocForDoc(entityId);
                ydoc = doc;
                docId = entityId;
                log.log('[docs_insert_table] Using document from service layer');
            }

            if (!ydoc || !docId) {
                return createErrorResult('Document not accessible', 'Could not access the document content.', 'execution');
            }
            
            const tableRows = [];
            for (let i = 0; i < rows; i++) {
                const cells = [];
                for (let j = 0; j < cols; j++) {
                    const cellType = hasHeader && i === 0 ? 'table_header' : 'table_cell';
                    cells.push({
                        type: cellType as NodeType,
                        content: [{
                            type: 'paragraph' as NodeType,
                            text: ''
                        }]
                    });
                }
                tableRows.push({
                    type: 'table_row' as NodeType,
                    content: cells
                });
            }

            const tableContent = [{
                type: 'table' as NodeType,
                content: tableRows
            }];

            const yjsContext = {
                yxml: ydoc.getXmlFragment('prosemirror'),
                doc: ydoc,
                docId: docId,
                userId: context.userId,
                metadata: {
                    windowId: resolved.windowId || '',
                    spaceId: context.spaceId,
                    suggestionMode: true
                }
            };

            const revisionId = new ObjectId().toString();
            const insertCommand = new Insert(tableContent, location, {
              revisionId,
              batchDescription: description || `Insert ${rows}x${cols} table`,
              suggestionMode: true
            });

            const executor = new CommandExecutor(yjsContext);
            const success = executor.execute(insertCommand);

            if (success) {
                saveToDatabase(yjsContext);
                return createSuccessResult(`Inserted ${rows}x${cols} table successfully`, {
                    success: true,
                    rows,
                    cols,
                    revisionId
                });
            } else {
                return createErrorResult('Table insertion failed', 'The table could not be inserted into the document.', 'execution');
            }

        } catch (error: any) {
            log.error('[docs_insert_table] ERROR:', error);
            return createErrorResult('Table insertion failed', error.message, 'execution');
        }
    }
};
